import React, { cloneElement, useId } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import useAnchoredTooltip from '../../hooks/useAnchoredTooltip';
import './ui.css';

const InfoIcon = () => (
    <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12" aria-hidden="true" focusable="false">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
    </svg>
);

const chain = (theirs, ours) => (e) => {
    theirs?.(e);
    ours(e);
};

/**
 * Accessible tooltip — the app's existing pattern (LotValueTile, AI price
 * header tip): a focusable trigger with aria-describedby pointing at a
 * role="tooltip" element that opens on hover, keyboard focus and tap, and
 * closes on leave, blur or Escape. Positioned `fixed` (useAnchoredTooltip) so
 * scrolling containers cannot clip it.
 *
 * Without children it renders a small (i) trigger named by `label`. With a
 * single child element (a button, a figure) it describes that element.
 */
export function Tooltip({ content, label, children }) {
    const tipId = `${useId().replace(/:/g, '')}-tip`;
    const { triggerRef, pos, open, close } = useAnchoredTooltip();

    // Escape closes an open tooltip and stops there, so it never also closes
    // the layer under it (the unit page goes back on Escape, D6).
    const onKeyDown = (e) => {
        if (e.key !== 'Escape' || !pos) return;
        e.stopPropagation();
        close();
    };

    const tipEl = (
        <span
            id={tipId}
            role="tooltip"
            className="v2-tip"
            hidden={!pos}
            style={pos ? { top: pos.top, left: pos.left } : undefined}
        >
            {content}
        </span>
    );
    // Rendered on <body> (html carries .ui-v2, so the styles still apply): a
    // trigger inside a sticky table header would otherwise trap the tip in the
    // header's stacking context, under the rows that scroll past it.
    const tip = typeof document === 'undefined' ? tipEl : createPortal(tipEl, document.body);

    if (children) {
        const child = React.Children.only(children);
        const describedBy = [child.props['aria-describedby'], tipId].filter(Boolean).join(' ');
        return (
            <span className="v2-tip-wrap" ref={triggerRef}>
                {cloneElement(child, {
                    'aria-describedby': describedBy,
                    onMouseEnter: chain(child.props.onMouseEnter, open),
                    onMouseLeave: chain(child.props.onMouseLeave, close),
                    onFocus: chain(child.props.onFocus, open),
                    onBlur: chain(child.props.onBlur, close),
                    onKeyDown: chain(child.props.onKeyDown, onKeyDown),
                })}
                {tip}
            </span>
        );
    }

    return (
        <span
            ref={triggerRef}
            className="v2-info"
            tabIndex={0}
            role="img"
            aria-label={label}
            aria-describedby={tipId}
            onMouseEnter={open}
            onMouseLeave={close}
            onFocus={open}
            onBlur={close}
            onKeyDown={onKeyDown}
            // A tap opens it; the click must not reach a row or sort button below.
            onClick={(e) => { e.stopPropagation(); open(); }}
        >
            <InfoIcon />
            {tip}
        </span>
    );
}

Tooltip.propTypes = {
    content: PropTypes.node.isRequired,
    label: PropTypes.string,
    children: PropTypes.element,
};
